import type { ChatMessage, ThreadItem } from '@/data/types';

// The pure parts of "Dr C suggests": which customer message needs an answer, the
// fixed question put to Dr Cleanstein about it, and splitting the answer into the
// account summary and the reply the user can drop into the message box.

/** The markers Dr C is asked to put around the suggested reply. */
export const REPLY_MARKER = 'Suggested reply:';
export const REPLY_END = 'End of reply.';

function isMessageItem(item: ThreadItem): item is ChatMessage {
  return item.from !== 'day' && item.from !== 'lead';
}

function textOf(m: ChatMessage): string {
  const b = m.body;
  if (b.kind === 'text') return b.text;
  if (b.kind === 'photo') return b.caption ? `a photo: ${b.caption}` : 'a photo';
  if (b.kind === 'voice') return 'a voice note';
  return b.text || b.title;
}

/**
 * The customer's latest message and whether it is still unanswered. Team notes and
 * system lines do not count as an answer; our reply or the auto reply does.
 */
export function lastCustomerMessage(thread: ThreadItem[]): { message: ChatMessage; text: string; unanswered: boolean } | null {
  let unanswered = true;
  for (let i = thread.length - 1; i >= 0; i -= 1) {
    const item = thread[i];
    if (!item || !isMessageItem(item)) continue;
    if (item.from === 'note' || item.from === 'system') continue;
    if (item.from === 'in') return { message: item, text: textOf(item), unanswered };
    unanswered = false;
  }
  return null;
}

/**
 * Everything the customer has written since our last reply, oldest first, as one answer is
 * owed to all of it: "Gaby 😂", "Mariagabrielle", "You know the Italians" is one point, not
 * three (Gary, 16 Oct). Falls back to their latest message when we replied after it.
 */
export function customerMessagesToAnswer(thread: ThreadItem[]): string {
  const burst: string[] = [];
  for (let i = thread.length - 1; i >= 0; i -= 1) {
    const item = thread[i];
    if (!item || !isMessageItem(item) || item.from === 'note' || item.from === 'system') continue;
    if (item.from !== 'in') break;
    burst.unshift(textOf(item).replace(/\s+/g, ' ').trim());
  }
  if (burst.length) return burst.filter(Boolean).join(' / ');
  return lastCustomerMessage(thread)?.text ?? '';
}

/** The last few lines of the chat, so Dr C does not repeat what was already said. */
export function recentChat(thread: ThreadItem[], max = 10): string[] {
  const lines: string[] = [];
  for (let i = thread.length - 1; i >= 0 && lines.length < max; i -= 1) {
    const item = thread[i];
    if (!item || !isMessageItem(item) || item.from === 'note' || item.from === 'system') continue;
    const who = item.from === 'in' ? 'Customer' : item.from === 'auto' ? 'Auto reply' : 'Us';
    lines.unshift(`${who}: ${textOf(item).replace(/\s+/g, ' ').slice(0, 200)}`);
  }
  return lines;
}

/**
 * The fixed question to Dr Cleanstein about what the customer has just written. WhatsApp is
 * short: a reply of one or two short sentences that does not repeat what the chat (including
 * the auto reply) already said (Gary, 14 Oct). It answers what the customer is saying and
 * looks up invoices or bookings only when they raised them: a joke about their name got a
 * request for proof of payment (Gaby, Gary 16 Oct).
 */
export function suggestQuestion(customerText: string, recent: string[] = []): string {
  const said = customerText.trim().slice(0, 800) || 'a message with no text';
  return [
    `What the customer has written since our last reply: "${said}".`,
    ...(recent.length ? ['The chat so far (oldest first):', ...recent.map((l) => `- ${l}`)] : []),
    'First work out what the customer is actually saying or asking, reading their messages together.',
    'Look up their invoices, payments or bookings only if those messages are about them. Never raise money, payment or anything else they did not bring up.',
    'Answer in this exact shape and nothing else:',
    'Facts: one short line with what you looked up and found, or "Nothing to look up." when nothing was needed.',
    REPLY_MARKER,
    'Two to four short, friendly WhatsApp lines in UK English that I can send, in the tone of the customer: sentences under 15 words, one idea per line, never a paragraph. Do not repeat anything the chat or the auto reply already said. No sign-off, no greeting line on its own.',
    REPLY_END,
    'Do not invent facts. If something is not in Sentinel, say so in the Facts line. No tables, no headings, no source line.',
  ].join('\n');
}

/**
 * A steer on the draft (Gary, 16 Oct): a follow-up in the same Dr C conversation, so the
 * customer's chat and the facts already found stay in mind. The draft as it now reads goes
 * with it, so edits made by hand are kept. Same shape as the first answer.
 */
export function steerQuestion(steer: string, draft: string): string {
  return [
    `Change the suggested reply as follows: "${steer.trim().slice(0, 500)}".`,
    ...(draft.trim() ? [`The reply currently reads: "${draft.trim().slice(0, 1000)}".`] : []),
    'Look anything up again only if the change needs it. Answer in the same shape as before and nothing else:',
    'Facts: one short line, only if something new matters.',
    REPLY_MARKER,
    'The new reply: two to four short, friendly WhatsApp lines in UK English that I can send, sentences under 15 words, never a paragraph. No sign-off, no greeting line on its own.',
    REPLY_END,
    'Do not invent facts.',
  ].join('\n');
}

/** Split Dr C's answer into the summary shown on the card and the reply to use. */
export function splitSuggestion(answer: string): { summary: string; reply: string | null } {
  const at = answer.toLowerCase().lastIndexOf(REPLY_MARKER.toLowerCase());
  if (at < 0) return { summary: answer.trim(), reply: null };
  let rest = answer.slice(at + REPLY_MARKER.length);
  // Stop at the end marker, or failing that at Dr C's usual "From ..." source line.
  const end = rest.toLowerCase().indexOf(REPLY_END.toLowerCase());
  if (end >= 0) rest = rest.slice(0, end);
  else rest = rest.split(/\n\s*\n(?=From |Only what |Source)/)[0] ?? rest;
  const reply = rest.replace(/^[\s*_"]+|[\s*_"]+$/g, '').trim();
  // Drop bold markers and a dangling "(2)" left just before the marker.
  let summary = answer.slice(0, at).trim();
  for (let prev = ''; prev !== summary;) {
    prev = summary;
    summary = summary.replace(/(\*\*|__)$/, '').replace(/\(2\)$/, '').trim();
  }
  return { summary, reply: reply || null };
}
